import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatAppDate } from "../../utils/appLanguage";
import { SUPPORT_EMAIL, legalLinks } from "../common/SiteFooter";
import AstriaRingLogo from "../astria/AstriaRingLogo";
import "./legal.css";

type Props = {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
};

/** Shared chrome for the Terms / Privacy / Refund pages. */
const LegalPageLayout: React.FC<Props> = ({ title, lastUpdated, children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="lg-page">
      <div className="lg-wrap">
        <header className="lg-top">
          <Link to="/" className="lg-brand">
            <AstriaRingLogo className="w-8 h-8" />
            Astria
          </Link>
          <button type="button" className="lg-back" onClick={() => navigate(-1)} aria-label={t("legal.goBack")}>
            <ArrowLeft size={15} />
            {t("legal.goBack")}
          </button>
        </header>

        <section className="lg-hero">
          <div className="lg-eyebrow">{t("footer.legal")}</div>
          <h1 className="lg-title">{title}</h1>
          <p className="lg-updated">
            {t("legal.lastUpdated", {
              date: formatAppDate(lastUpdated, { year: "numeric", month: "long", day: "numeric" }),
            })}
          </p>
          <nav className="lg-tabs" aria-label={t("footer.legal")}>
            {legalLinks.map((l) => (
              <Link key={l.to} to={l.to} className="lg-tab" aria-current={pathname === l.to ? "page" : undefined}>
                {t(l.labelKey)}
              </Link>
            ))}
          </nav>
        </section>
      </div>

      <main className="lg-main">
        <div className="lg-wrap">
          <div className="lg-body">{children}</div>
        </div>
      </main>

      <footer className="lg-foot">
        <div className="lg-wrap">
          <span>{t("footer.rights", { year: new Date().getFullYear() })}</span>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </div>
      </footer>
    </div>
  );
};

/** Section heading + body used by every legal page. */
export const LegalSection: React.FC<{
  heading: string;
  children: React.ReactNode;
}> = ({ heading, children }) => (
  <section className="lg-section">
    <h2>{heading}</h2>
    {children}
  </section>
);

export const LegalList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="lg-list">
    {items.map((item, i) => (
      <li key={i}>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default LegalPageLayout;

/** Renders a translated paragraph, turning "{{email}}" into a mailto link. */
export const LegalText: React.FC<{ k: string }> = ({ k }) => {
  const { t } = useTranslation();
  const TOKEN = "@@EMAIL@@";
  const parts = t(k, { email: TOKEN }).split(TOKEN);
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <a href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

/** Bold lead-in followed by body text (refund eligibility list). */
export const LegalLead: React.FC<{ k: string }> = ({ k }) => {
  const { t } = useTranslation();
  return (
    <>
      <strong>{t(`${k}.lead`)}</strong>{" "}
      {t(`${k}.body`)}
    </>
  );
};
